import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { errMsg } from "./LoadError";

export const OUTCOMES = ["Contacted", "No Answer", "Left Voicemail", "Appointment Booked", "Follow-up Required", "Not Interested", "Not Proceeding"];
const CHANNELS = ["Call", "Text", "WhatsApp", "Email", "Meeting"];
const iso = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const QUICK = [["Tomorrow", 1], ["+2 days", 2], ["+3 days", 3], ["+1 week", 7], ["+2 weeks", 14]];
const DEFAULT_NEXT = { "No Answer": 1, "Left Voicemail": 2, "Contacted": 3, "Follow-up Required": 3 };
const inputCls = "w-full bg-[#091E16] border border-white/15 rounded-lg px-3 py-2.5 text-base sm:text-sm text-white outline-none focus:border-brass";

const Chip = ({ on, children, ...p }) => (
  <button type="button" {...p} className={`text-sm rounded-full px-3.5 py-2 border transition-colors ${on ? "bg-brass text-emerald-950 border-brass font-semibold" : "border-white/15 text-emerald-100/80 hover:bg-white/5"}`}>{children}</button>
);

// mode: "outcome" | "appointment" | "followup"
export function OutcomeSheet({ open, onOpenChange, target, mode = "outcome", onSaved }) {
  const [outcome, setOutcome] = useState("");
  const [channel, setChannel] = useState("Call");
  const [note, setNote] = useState("");
  const [next, setNext] = useState("");
  const [appt, setAppt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOutcome(mode === "appointment" ? "Appointment Booked" : "");
    setChannel("Call"); setNote(""); setAppt(""); setNext(mode === "followup" ? iso(1) : "");
  }, [open, mode]);

  const pick = (o) => { setOutcome(o); if (DEFAULT_NEXT[o] && !next) setNext(iso(DEFAULT_NEXT[o])); };

  const save = async () => {
    if (mode === "followup") {
      if (!next) return toast.error("Choose a follow-up date");
      setBusy(true);
      try {
        await api.post(`/crm/unified-contacts/${target.store}/${target.id}/followup`, { date: next, note });
        toast.success("Follow-up set"); onOpenChange(false); onSaved && onSaved();
      } catch (e) { toast.error(errMsg(e, "Could not save")); }
      finally { setBusy(false); }
      return;
    }
    if (!outcome) return toast.error("Choose an outcome");
    if (outcome === "Appointment Booked" && !appt) return toast.error("Choose the appointment date and time");
    setBusy(true);
    try {
      await api.post(`/crm/unified-contacts/${target.store}/${target.id}/outcome`,
        { outcome, channel, note, next_date: next || null, appointment_at: outcome === "Appointment Booked" ? appt : "" });
      toast.success("Saved");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="bg-[#0B241B] border-white/10 text-white max-h-[92vh] overflow-y-auto rounded-t-2xl sm:max-w-xl sm:mx-auto" data-testid="outcome-sheet">
        <SheetHeader><SheetTitle className="text-white font-serif text-left">{target?.name || "Record outcome"}</SheetTitle></SheetHeader>
        <div className="space-y-5 mt-4 pb-4">
          {mode === "followup" && <p className="text-xs text-emerald-100/60" data-testid="followup-only-hint">Schedule the next follow-up. This does not mark the customer as contacted.</p>}
          {mode !== "followup" && <>
          <div>
            <div className="text-xs text-emerald-100/50 mb-2">How did you contact them?</div>
            <div className="flex flex-wrap gap-2">{CHANNELS.map((c) => <Chip key={c} on={channel === c} onClick={() => setChannel(c)} data-testid={`outcome-channel-${c.toLowerCase()}`}>{c}</Chip>)}</div>
          </div>
          <div>
            <div className="text-xs text-emerald-100/50 mb-2">Outcome</div>
            <div className="flex flex-wrap gap-2">{OUTCOMES.map((o) => <Chip key={o} on={outcome === o} onClick={() => pick(o)} data-testid={`outcome-${o.toLowerCase().replace(/\s/g, "-")}`}>{o}</Chip>)}</div>
          </div>
          </>}
          {outcome === "Appointment Booked" && (
            <label className="block"><span className="text-xs text-emerald-100/50">Appointment date & time</span>
              <input type="datetime-local" value={appt} onChange={(e) => setAppt(e.target.value)} className={`${inputCls} mt-1.5`} data-testid="outcome-appointment-input" /></label>
          )}
          <label className="block"><span className="text-xs text-emerald-100/50">Note (optional)</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was said, what's next…" className={`${inputCls} mt-1.5`} data-testid="outcome-note" /></label>
          <div>
            <div className="text-xs text-emerald-100/50 mb-2">Next follow-up</div>
            <div className="flex flex-wrap gap-2 mb-2">{QUICK.map(([l, n]) => <Chip key={l} on={next === iso(n)} onClick={() => setNext(iso(n))} data-testid={`outcome-next-${n}`}>{l}</Chip>)}
              <Chip on={!next} onClick={() => setNext("")} data-testid="outcome-next-none">None</Chip></div>
            <input type="date" value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="outcome-next-date" />
          </div>
          {["Not Interested", "Not Proceeding"].includes(outcome) && <p className="text-xs text-amber-200/80">To close this lead, use <b>Change status</b> afterwards and choose a lost reason.</p>}
          <button onClick={save} disabled={busy} data-testid="outcome-save"
            className="w-full inline-flex items-center justify-center gap-2 bg-brass hover:brightness-110 text-emerald-950 font-semibold rounded-xl h-12 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}Save
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
